// Pure helpers for the Funnels App launch tab (KET-8425). Every figure is the funnel stats API's
// (I-4): these functions order, label and word things; they never compute a rate from counts.
// Range helpers (RANGES, RANGE_LABELS, parseRange, rangeTitle, rangeWindow) are Onboarding's.

import { formatCount } from "./format";
import { rangeTitle, rangeWindow, type Range } from "./onboarding-format";
import type { AppLaunchEmail, AppLaunchStep, FunnelAppLaunchStats } from "./stats-api";

/** The app launch day: the default start of every range on this tab. */
export const APP_LAUNCH_DAY = "2026-10-27";

export const NO_BIGGEST_LOSS_TEXT = "No screen has 30 or more people yet.";
export const APP_LAUNCH_FLOOR_CAPTION = "Biggest loss only counts screens reached by 30 or more people.";
export const APP_LAUNCH_LOSS_CAPTION =
  "Loss rate = (reached − completed) ÷ reached: people who reached the screen and did not complete it. In progress = of those, people active in the last 24 hours.";
export const APP_LAUNCH_EMPTY_TEXT = "No app launch traffic in this window yet. Real customers start at 16:00 UTC on 27 Oct.";
export const APP_LAUNCH_ERROR_TITLE = "App launch figures unavailable";
export const LAUNCH_KPIS_CAPTION =
  "Real people only, team test traffic left out. Each row counts people who passed that step, so a row is never higher than the step it depends on. Account created includes people who signed in to an existing account. Order Made and Precision Home reached both depend on Account created, so their rate from previous step is measured against it. Order Made = an in-app order, or a Shopify app order matched to the person.";
export const LAUNCH_KPIS_PENDING_TEXT = "The launch KPIs appear once the funnel stats update is live.";
export const NO_DESIRED_ACTION_TEXT = "—";
export const NO_DESIRED_ACTION_TITLE = "This email has no desired action";

/**
 * The date window a range names. Same as Onboarding's, but "Since launch" starts on the app launch
 * day, and before it the window closes on launch day instead of running backwards (an empty read).
 */
export function appLaunchWindow(
  range: Range,
  today: Date,
  custom: { from?: unknown; to?: unknown } = {},
): { from: string; to: string } | { error: string } {
  if (range !== "launch") return rangeWindow(range, today, custom);
  const to = today.toISOString().slice(0, 10);
  return { from: APP_LAUNCH_DAY, to: to < APP_LAUNCH_DAY ? APP_LAUNCH_DAY : to };
}

/** A served rate (a fraction) to one decimal, or a dash when the service sent none. */
export function formatRate(value: number | null | undefined): string {
  return value == null ? "—" : `${(value * 100).toFixed(1)}%`;
}

export function count(value: number | null | undefined): string {
  return value == null ? "—" : formatCount(value);
}

/** "Since launch (Tue 27 Oct): 130 people opened the app · 50 signed up · 6 placed an order" */
export function appLaunchSummaryLine(stats: FunnelAppLaunchStats, range: Range, window: { from: string; to: string }): string {
  const title = rangeTitle(range, window);
  const m = stats.milestones;
  const parts = [`${count(m.app_entry)} ${m.app_entry === 1 ? "person" : "people"} opened the app`, `${count(m.sign_up)} signed up`];
  if (stats.orders.order_placed != null) parts.push(`${formatCount(stats.orders.order_placed)} placed an order`);
  return `${title}: ${parts.join(" · ")}`;
}

/** Whether a step row is the one the service named the biggest loss (flow and step id). */
export function isBiggestLoss(step: Pick<AppLaunchStep, "flow" | "step_id">, biggest: FunnelAppLaunchStats["biggest_loss"]): boolean {
  return biggest !== null && biggest.flow === step.flow && biggest.step_id === step.step_id;
}

/** "Biggest loss: goal (onboarding, intake): 15 of 120 lost (12.5%)". Stage and reached come from the matching step row. */
export function biggestLossLine(stats: Pick<FunnelAppLaunchStats, "biggest_loss" | "step_loss">): string {
  const b = stats.biggest_loss;
  if (!b) return NO_BIGGEST_LOSS_TEXT;
  const row = stats.step_loss.find((s) => isBiggestLoss(s, b));
  const where = row ? `${b.flow}, ${row.stage}` : b.flow;
  const reached = row?.people_reached == null ? "" : ` of ${formatCount(row.people_reached)}`;
  return `Biggest loss: ${b.step_id} (${where}): ${formatCount(b.people_lost)}${reached} lost (${formatRate(b.loss_rate)})`;
}

export const APP_LAUNCH_STEPS_CAPTION =
  "App Front Door screens in order. 0 means nobody reached that screen in this window; screens only on other doors are not listed.";

/** The # shown for a step: its place on the door's path, else the app's own index. */
export function stepNumber(s: AppLaunchStep): string {
  const n = s.position ?? s.step_index;
  return n == null ? "" : String(n);
}

/** Flow step order: the door's catalog screens by position, then other onboarding screens by index, then check-in. */
export function flowOrder(a: AppLaunchStep, b: AppLaunchStep): number {
  const flow = (s: AppLaunchStep) => (s.flow === "onboarding" ? 0 : 1);
  const catalog = (s: AppLaunchStep) => (s.position != null ? 0 : 1);
  const n = (s: AppLaunchStep) => s.position ?? s.step_index ?? 0;
  return flow(a) - flow(b) || catalog(a) - catalog(b) || n(a) - n(b) || a.step_id.localeCompare(b.step_id);
}

/** The step rows in flow step order, or by loss rate. A row with no rate sits last whichever way the reader sorts. */
export function stepRows(steps: AppLaunchStep[], sortByLoss: "asc" | "desc" | null): AppLaunchStep[] {
  if (!sortByLoss) return [...steps].sort(flowOrder);
  return [...steps].sort((a, b) => {
    if (a.loss_rate == null || b.loss_rate == null) return (a.loss_rate == null ? 1 : 0) - (b.loss_rate == null ? 1 : 0);
    const gap = b.loss_rate - a.loss_rate;
    return sortByLoss === "desc" ? gap : -gap;
  });
}

/** Bar width as a share of the largest served loss rate, so the worst step fills its cell. */
export function barShare(step: Pick<AppLaunchStep, "loss_rate">, steps: AppLaunchStep[]): number {
  if (step.loss_rate == null) return 0;
  const rates = steps.filter((s) => s.loss_rate != null).map((s) => s.loss_rate as number);
  const top = Math.max(0, ...rates);
  if (top === 0) return 0;
  return Math.min(100, Math.round((step.loss_rate / top) * 100));
}

export function emailLabel(row: Pick<AppLaunchEmail, "email_type" | "email_key">): string {
  return `${row.email_type} · ${row.email_key}`;
}

/** The empty state: no step loss, no email and nobody in the milestones block means nobody has come yet. */
export function appLaunchEmpty(stats: Pick<FunnelAppLaunchStats, "step_loss" | "email" | "milestones">): string | null {
  return stats.step_loss.length === 0 && stats.email.length === 0 && !stats.milestones.people ? APP_LAUNCH_EMPTY_TEXT : null;
}
